import React, {startTransition, useState} from "react";
import {useQueryClient} from "react-query";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {levelSelector, tokenSelector} from "../../Recoil/Atom/authState";
import * as boardApi from "../../api/boardApi/boardApi";
import * as postApi from "../../api/boardApi/postAip";

import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseItem, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {SubMenuGroup} from "../../styledComponents/StyledListContainer";
import BusinessIcon from "@mui/icons-material/Business";
import {
  BoardPostListParams,
  KPBoard,
  initializeBoardListParams,
} from "../../models/serverModels/board/KPBoard";
import {Controller, useForm} from "react-hook-form";
import {
  InitializeBoardSubCategory,
  KPBoardSubCategory,
} from "../../models/serverModels/board/KPBoardSubCategory";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1200px",
}));

const PostList = () => {
  const {id} = useParams();

  const tokenState = useRecoilValue(tokenSelector);
  const userLevel = useRecoilValue(levelSelector);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAlertState = useSetRecoilState(alertStateAtom);

  const [category, setCategory] = useState<KPBoardSubCategory[]>([]);
  const [filter, setFilter] = useState<BoardPostListParams>({
    ...initializeBoardListParams(parseInt(id!)),
  });

  const {
    data: board,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseItem<KPBoard>>({
    key: ["getBoardWithPosts", {...filter, token: tokenState}],
    api: () =>
      boardApi.getBoardWithPosts({
        ...filter,
        token: tokenState,
      }),
    options: {
      onSuccess: (data: ResponseItem<KPBoard>) => {
        setCategory(data?.item?.subCategories!);
      },
    },
  });
  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: postApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("getBoardWithPosts");
      },
    },
  });

  const {control, reset, setValue} = useForm();

  const FilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const selectChangeHandler = (event: SelectChangeEvent<any>) => {
    startTransition(() => {
      setValue(event.target.name, event.target?.value);
      setFilter({...filter, page: 1, [event.target.name]: event.target?.value});
    });
  };

  const resetHandler = () => {
    setFilter(initializeBoardListParams(parseInt(id!)));
    reset();
  };

  return (
    <ListContainer>
      <SubTitle subTitle={board?.item?.name ? board?.item?.name + " - 글 목록" : "게시판"} />
      <SubMenuGroup>
        <Box display="flex" alignItems="center" gap="0 6px">
          <Controller
            name="searchWord"
            control={control}
            render={({field}) => (
              <TextField
                {...field}
                label="게시글"
                size="small"
                placeholder="게시글 검색"
                onChange={FilteringHandler}
              />
            )}
          />
          <Box>
            <FormControl>
              <InputLabel id="subCategoryUID">카테고리</InputLabel>
              <Select
                style={{minWidth: "200px"}}
                name="subCategoryUID"
                id="subCategoryUID"
                label="카테고리"
                size="small"
                value={filter.subCategoryUID}
                onChange={selectChangeHandler}
              >
                <MenuItem value={-1}>전체</MenuItem>
                {category?.map((item) => (
                  <MenuItem key={item.uid} value={item?.uid}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </Box>
        {userLevel === 5 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<BusinessIcon />}
            onClick={() => {
              navigate(`/Post/Create/${board?.item?.uid}/${board?.item?.name}`);
            }}
          >
            {board?.item?.name} 추가
          </Button>
        )}
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">제목</TableCell>
              <TableCell align="center">작성일자</TableCell>
              <TableCell align="center">작성자</TableCell>
              {userLevel === 5 && (
                <TableCell align="center" width="20%">
                  편집
                </TableCell>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={5} />
            ) : (
              board?.item?.posts.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    <Link to={`/Post/Read/${item.uid}`}>{item.title}</Link>
                  </TableCell>
                  <TableCell align="center">
                    {new Date(item?.entryDate).getFullYear()}년
                    {new Date(item?.entryDate).getMonth() + 1}월
                    {new Date(item?.entryDate).getDate()}일
                  </TableCell>
                  <TableCell align="center">{item.writerName}</TableCell>
                  {userLevel === 5 && (
                    <TableCell align="center">
                      <Box>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          style={{marginRight: "12px"}}
                          onClick={() => {
                            navigate(`/Post/Edit/${item.uid}/${board?.item?.name}`);
                          }}
                        >
                          편집
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            setAlertState({
                              toggle: true,
                              message: "등록된 게시글을 삭제하시겠습니까?",
                              execution: () => removeMutate({token: tokenState, id: item.uid!}),
                            });
                          }}
                        >
                          삭제
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </ListContainer>
  );
};

export default PostList;
